import { useEffect, useMemo, useState, useRef } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from "react-simple-maps";
import { STATUS_COLORS, api } from "@/lib/nexus";
import AgentInspector from "@/components/AgentInspector";
import NlpQueryBar from "@/components/NlpQueryBar";
import { MapPin, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

const WORLD_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export default function GlobalMap() {
  const [agents, setAgents] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [nlpActive, setNlpActive] = useState(null);
  const [hover, setHover] = useState(null);
  const [view, setView] = useState({ coordinates: [15, 20], zoom: 1 });

  const loadAll = () => {
    api.get("/geo/agents").then((r) => setAgents(r.data.items));
    api.get("/regions").then((r) => setRegions(r.data.regions));
  };
  useEffect(() => { loadAll(); }, []);

  const onQueryResult = (r) => {
    setNlpActive(r);
    // Filter our client-side geo agents based on server-returned agent ids
    const ids = new Set(r.nodes.map((n) => n.id));
    // Refetch geo but filtered — reuse filters when possible
    const p = new URLSearchParams();
    Object.entries(r.filters || {}).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") p.append(k, String(v));
    });
    api.get(`/geo/agents?${p}`).then((res) => {
      const items = res.data.items.filter((a) => ids.size === 0 || ids.has(a.id) || res.data.items.length < 12);
      setAgents(items.length ? items : res.data.items);
      // Auto-zoom to bounding box of items
      if (res.data.items.length) {
        const lats = res.data.items.map((a) => a.lat);
        const lngs = res.data.items.map((a) => a.lng);
        const avgLat = lats.reduce((s, x) => s + x, 0) / lats.length;
        const avgLng = lngs.reduce((s, x) => s + x, 0) / lngs.length;
        const spread = Math.max(
          Math.max(...lats) - Math.min(...lats),
          Math.max(...lngs) - Math.min(...lngs),
        );
        const z = spread < 5 ? 6 : spread < 25 ? 3 : spread < 60 ? 2 : 1.4;
        setView({ coordinates: [avgLng, avgLat], zoom: z });
      }
    });
  };
  const onQueryClear = () => { setNlpActive(null); setView({ coordinates: [15, 20], zoom: 1 }); loadAll(); };

  const grouped = useMemo(() => {
    // Group agents by region for compact display when zoomed out
    const map = new Map();
    agents.forEach((a) => {
      const key = a.region;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    return map;
  }, [agents]);

  const zoom = (factor) => setView((v) => ({ ...v, zoom: Math.max(0.8, Math.min(12, v.zoom * factor)) }));

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <div className="panel-title">GLOBAL MAP // AGENT GEOGRAPHY</div>
          <h1 className="font-rajdhani font-bold text-2xl tracking-tight mt-1">
            Where the agentic economy <span className="text-nexus-cyan">actually lives.</span>
          </h1>
        </div>
        <div className="text-[10px] font-mono text-nexus-text-3 uppercase tracking-widest">
          {agents.length} agents · {regions.length} regions · zoom {view.zoom.toFixed(1)}x
        </div>
      </div>

      <NlpQueryBar onResult={onQueryResult} onClear={onQueryClear} active={nlpActive} />

      <div className="relative brackets border border-nexus-border bg-nexus-panel/30 overflow-hidden" style={{ height: "calc(100vh - 300px)", minHeight: 560 }}>
        <span className="b1" /><span className="b2" />

        <div className="absolute top-2 left-3 z-10 panel-title"><span className="dot" />Datacenter Regions</div>
        <div className="absolute top-2 right-3 z-10 flex items-center gap-1.5">
          <button data-testid="map-zoom-in" onClick={() => zoom(1.4)} className="border border-nexus-border p-1 hover:border-nexus-cyan hover:text-nexus-cyan text-nexus-text-2 bg-nexus-panel/70"><ZoomIn size={12} /></button>
          <button data-testid="map-zoom-out" onClick={() => zoom(1 / 1.4)} className="border border-nexus-border p-1 hover:border-nexus-cyan hover:text-nexus-cyan text-nexus-text-2 bg-nexus-panel/70"><ZoomOut size={12} /></button>
          <button data-testid="map-zoom-reset" onClick={() => setView({ coordinates: [15, 20], zoom: 1 })} className="border border-nexus-border p-1 hover:border-nexus-cyan hover:text-nexus-cyan text-nexus-text-2 bg-nexus-panel/70"><Maximize2 size={12} /></button>
        </div>

        <ComposableMap
          projection="geoEqualEarth"
          projectionConfig={{ scale: 175 }}
          style={{ width: "100%", height: "100%" }}
        >
          <ZoomableGroup
            center={view.coordinates}
            zoom={view.zoom}
            onMoveEnd={(pos) => setView(pos)}
            minZoom={0.8}
            maxZoom={12}
          >
            <Geographies geography={WORLD_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: { fill: "#0A1122", stroke: "#1E293B", strokeWidth: 0.5, outline: "none" },
                      hover: { fill: "#111A30", stroke: "#334155", outline: "none" },
                      pressed: { fill: "#111A30", outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>

            {/* Region halos */}
            {regions.map((r) => (
              <Marker key={r.code} coordinates={[r.lng, r.lat]}>
                <circle
                  r={Math.min(22, 6 + Math.sqrt(r.count) * 2.5) / view.zoom}
                  fill="#00F0FF"
                  fillOpacity={0.06}
                  stroke="#00F0FF"
                  strokeOpacity={0.35}
                  strokeWidth={0.5 / view.zoom}
                />
                <text
                  y={-14 / view.zoom}
                  textAnchor="middle"
                  style={{
                    fontFamily: "Rajdhani, sans-serif",
                    fontSize: 8 / Math.sqrt(view.zoom),
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    fill: "#94A3B8",
                    pointerEvents: "none",
                  }}
                >
                  {r.name.split(" (")[0]}
                </text>
              </Marker>
            ))}

            {/* Agent dots */}
            {agents.map((a) => {
              const c = STATUS_COLORS[a.status];
              return (
                <Marker
                  key={a.id}
                  coordinates={[a.lng, a.lat]}
                  onClick={() => setSelectedId(a.id)}
                  onMouseEnter={() => setHover(a)}
                  onMouseLeave={() => setHover(null)}
                >
                  <circle
                    data-testid={`map-agent-${a.id}`}
                    r={(a.status !== "normal" ? 3.4 : 2.4) / Math.sqrt(view.zoom)}
                    fill={c}
                    stroke="#040914"
                    strokeWidth={0.6 / view.zoom}
                    style={{ cursor: "pointer" }}
                  />
                  {a.status !== "normal" && (
                    <circle
                      r={6 / Math.sqrt(view.zoom)}
                      fill={c}
                      fillOpacity={0.15}
                    />
                  )}
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>

        {/* Legend */}
        <div className="absolute bottom-3 left-3 panel px-3 py-2 text-[10px] font-mono uppercase tracking-widest z-10 flex flex-col gap-1">
          <div className="text-nexus-text-3">Legend</div>
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-nexus-cyan" /> Normal</div>
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-nexus-amber" /> Watch</div>
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-nexus-rose" /> Critical</div>
        </div>

        {hover && !selectedId && (
          <div className="absolute bottom-3 right-3 panel px-3 py-2 text-[11px] font-mono max-w-[280px] z-10">
            <div className="text-nexus-cyan font-rajdhani font-semibold tracking-widest uppercase text-[12px]">{hover.name}</div>
            <div className="text-nexus-text-2 mt-1">{hover.framework} · {hover.chain}</div>
            <div className="text-nexus-text-2 flex items-center gap-1 mt-0.5"><MapPin size={10} /> {hover.region_name}</div>
            <div className="text-nexus-text-3 mt-1 text-[10px] uppercase tracking-widest">click to inspect →</div>
          </div>
        )}

        <AgentInspector
          agentId={selectedId}
          onClose={() => setSelectedId(null)}
          onTrace={(id) => { setSelectedId(null); window.location.assign(`/graph`); void id; }}
        />
      </div>
    </div>
  );
}
